import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../api";
import { CalendarClock, Plus, Trash2, CheckCircle2, AlertCircle, Pencil, MessageCircle, FileDown } from "lucide-react";
import { generatePlansReport } from "../../pdfReports";

const EMPTY = {
  client_name: "", plan_type: "mensal", price: 0,
  start_date: new Date().toISOString().slice(0,10),
  next_billing: new Date(new Date().setMonth(new Date().getMonth()+1)).toISOString().slice(0,10),
  status: "paga", phone: "", notes: "", active: true,
};
const brl = (n) => (n || 0).toLocaleString("pt-BR", { style:"currency", currency:"BRL" });

export default function PlansTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);

  const load = () => { api.get("/admin/plans").then(r=>setItems(r.data)); };
  useEffect(load, []);

  const save = async () => {
    if (!form.client_name) return toast.error("Nome obrigatório");
    try {
      if (editingId) {
        await api.patch(`/admin/plans/${editingId}`, { ...form, price: Number(form.price)||0 });
        toast.success("Plano atualizado");
      } else {
        await api.post("/admin/plans", { ...form, price: Number(form.price)||0 });
        toast.success("Plano cadastrado");
      }
      setForm(EMPTY); setEditingId(null); load();
    } catch { toast.error("Falha ao salvar"); }
  };
  const startEdit = (p) => { setEditingId(p.plan_id); setForm({...p}); };
  const cancelEdit = () => { setEditingId(null); setForm(EMPTY); };
  const del = async (id) => { if (!window.confirm("Excluir plano?")) return; await api.delete(`/admin/plans/${id}`); load(); };
  const toggleStatus = async (id) => { await api.patch(`/admin/plans/${id}/status`); toast.success("Status atualizado"); load(); };

  const waCharge = (p) => {
    if (!p.phone) { toast.error("Cadastre o telefone da cliente primeiro"); return; }
    const phone = String(p.phone).replace(/\D/g, "");
    const first = (p.client_name || "").split(" ")[0];
    const valor = (p.price || 0).toLocaleString("pt-BR", { style:"currency", currency:"BRL" });
    const msg = `Oi ${first}! Aqui é do Arya Studio 🌻 Passando pra lembrar da sua mensalidade do plano ${p.plan_type} (${valor}), com vencimento em ${p.next_billing}. Consegue confirmar o pagamento por aqui? Qualquer dúvida, estou à disposição!`;
    window.open(`https://wa.me/${phone.startsWith("55")?phone:"55"+phone}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 card-arya overflow-hidden">
        <div className="p-4 border-b border-[var(--line)] flex items-center justify-between">
          <p className="text-xs tracking-widest font-display text-[color:var(--sun-700)]">CLIENTES COM PLANO ATIVO</p>
          <button onClick={()=>generatePlansReport({ plans: items })} className="btn-ghost !py-1 !px-3 text-xs inline-flex items-center gap-1" data-testid="btn-pdf-plans">
            <FileDown size={12}/> Baixar PDF
          </button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[var(--cream-100)] text-left text-xs tracking-widest font-display">
            <tr><th className="p-4">CLIENTE</th><th className="p-4">PLANO</th><th className="p-4">VALOR</th><th className="p-4">PRÓX. COBRANÇA</th><th className="p-4">STATUS</th><th className="p-4">AÇÕES</th></tr>
          </thead>
          <tbody className="divide-y divide-[var(--line)]">
            {items.map(p => (
              <tr key={p.plan_id} data-testid={`plan-${p.plan_id}`}>
                <td className="p-4">
                  <div className="font-display font-semibold">{p.client_name}</div>
                  <div className="text-xs text-[color:var(--ink-500)]">{p.phone || "—"} · desde {p.start_date}</div>
                </td>
                <td className="p-4 capitalize">{p.plan_type}</td>
                <td className="p-4">{brl(p.price)}</td>
                <td className="p-4">{p.next_billing}</td>
                <td className="p-4">
                  <button onClick={()=>toggleStatus(p.plan_id)} data-testid={`plan-status-${p.plan_id}`}
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${p.status==='paga'?'text-[color:var(--success)] border-[color:var(--success)]/40 bg-[rgba(74,124,62,.08)]':'text-[color:var(--danger)] border-[color:var(--danger)]/40 bg-[rgba(179,58,58,.08)]'}`}>
                    {p.status==='paga' ? <><CheckCircle2 size={12}/> Paga</> : <><AlertCircle size={12}/> Atrasada</>}
                  </button>
                </td>
                <td className="p-4 flex gap-1">
                  {p.status === "atrasada" && (
                    <button onClick={()=>waCharge(p)} className="p-1.5 rounded-full border border-[color:var(--success)] text-[color:var(--success)]" title="Cobrar via WhatsApp" data-testid={`wa-plan-${p.plan_id}`}><MessageCircle size={14}/></button>
                  )}
                  <button onClick={()=>startEdit(p)} className="p-1.5 border border-[var(--line)] rounded-full text-[color:var(--sun-700)]" data-testid={`edit-plan-${p.plan_id}`}><Pencil size={14}/></button>
                  <button onClick={()=>del(p.plan_id)} className="p-1.5 text-[color:var(--danger)]" data-testid={`del-plan-${p.plan_id}`}><Trash2 size={14}/></button>
                </td>
              </tr>
            ))}
            {items.length===0 && <tr><td colSpan="6" className="p-8 text-center text-[color:var(--ink-500)]">Nenhum plano cadastrado.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="card-arya p-6 h-fit">
        <h3 className="font-serif text-xl mb-3 flex items-center gap-2"><CalendarClock size={16}/> {editingId ? "Editar plano" : "Novo plano"}</h3>
        <div className="space-y-2">
          <input placeholder="Nome da cliente" value={form.client_name} onChange={e=>setForm({...form, client_name:e.target.value})}
                 className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm" data-testid="plan-name"/>
          <input placeholder="Telefone" value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})}
                 className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm"/>
          <select value={form.plan_type} onChange={e=>setForm({...form, plan_type:e.target.value})}
                  className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm" data-testid="plan-type">
            <option value="semanal">Semanal</option>
            <option value="quinzenal">Quinzenal</option>
            <option value="mensal">Mensal</option>
          </select>
          <input type="number" step="0.01" placeholder="Valor (R$)" value={form.price} onChange={e=>setForm({...form, price:e.target.value})}
                 className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm" data-testid="plan-price"/>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] tracking-widest text-[color:var(--ink-500)]">INÍCIO</label>
              <input type="date" value={form.start_date} onChange={e=>setForm({...form, start_date:e.target.value})}
                     className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm"/>
            </div>
            <div>
              <label className="text-[10px] tracking-widest text-[color:var(--ink-500)]">PRÓX. COBRANÇA</label>
              <input type="date" value={form.next_billing} onChange={e=>setForm({...form, next_billing:e.target.value})}
                     className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm"/>
            </div>
          </div>
          <select value={form.status} onChange={e=>setForm({...form, status:e.target.value})}
                  className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm">
            <option value="paga">Mensalidade PAGA</option>
            <option value="atrasada">Mensalidade ATRASADA</option>
          </select>
          <textarea rows="2" placeholder="Observações" value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})}
                    className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm"/>
          <div className="flex gap-2">
            <button onClick={save} className="btn-gold flex-1 inline-flex items-center justify-center gap-1" data-testid="btn-save-plan"><Plus size={14}/> {editingId ? "Salvar" : "Cadastrar"}</button>
            {editingId && <button onClick={cancelEdit} className="btn-ghost">Cancelar</button>}
          </div>
        </div>
      </div>
    </div>
  );
}
